import React from 'react';
import { Box, Grid, Button, Typography, Fade, Chip, CircularProgress } from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  CheckCircle as CheckCircleIcon,
  OpenInNew as OpenInNewIcon,
  FilePresent as FilePresentIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  PlaceOutlined as PlaceOutlinedIcon,
  LocalOfferOutlined as LocalOfferOutlinedIcon,
  CachedOutlined as CachedOutlinedIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  HistoryOutlined as HistoryOutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  CalendarMonthOutlined as CalendarMonthOutlinedIcon,
  AssignmentOutlined as AssignmentOutlinedIcon,
} from '@mui/icons-material';
import { BotMessage } from '../hooks/useChatbotState';

export interface QuickAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  query: string;
}

export const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'create-shipment',
    label: 'Create Shipment',
    icon: <Inventory2OutlinedIcon sx={{ fontSize: 20 }} />,
    query: 'I want to create a new shipment',
  },
  {
    id: 'track-shipment',
    label: 'Track Shipment',
    icon: <PlaceOutlinedIcon sx={{ fontSize: 20 }} />,
    query: 'Can you help me track my shipment?',
  },
  {
    id: 'get-rates',
    label: 'Get Shipping Rates',
    icon: <LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />,
    query: 'Show me shipping rates',
  },
  {
    id: 'repeat-previous',
    label: 'Repeat Previous',
    icon: <CachedOutlinedIcon sx={{ fontSize: 20 }} />,
    query: 'Repeat my previous shipment',
  },
  {
    id: 'create-invoice',
    label: 'Create Invoice',
    icon: <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />,
    query: 'I need to create an invoice',
  },
  {
    id: 'download-labels',
    label: 'Download Labels',
    icon: <DownloadOutlinedIcon sx={{ fontSize: 20 }} />,
    query: 'Download shipping labels',
  },
  {
    id: 'shipment-history',
    label: 'Shipment History',
    icon: <HistoryOutlinedIcon sx={{ fontSize: 20 }} />,
    query: 'Show my shipment history',
  },
  {
    id: 'delivery-status',
    label: 'Delivery Status',
    icon: <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />,
    query: 'What is the delivery status?',
  },
  {
    id: 'pickup-request',
    label: 'Pickup Request',
    icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />,
    query: 'I need to request a pickup',
  },
  {
    id: 'customs-docs',
    label: 'Customs Docs',
    icon: <AssignmentOutlinedIcon sx={{ fontSize: 20 }} />,
    query: 'Upload customs documents',
  },
];

interface ChatMessagesProps {
  messages: BotMessage[];
  isLoading: boolean;
  isActivating: boolean;
  handleQuickAction: (action: QuickAction) => void;
  handleBotStateAction: (action: 'yes' | 'no' | 'open-form', url?: string) => void;
  handleRelatedChipClick: (chip: any) => void;
  messagesEndRef: React.RefObject<HTMLDivElement>;
}

export const ChatMessages: React.FC<ChatMessagesProps> = React.memo(
  ({
    messages,
    isLoading,
    isActivating,
    handleQuickAction,
    handleBotStateAction,
    handleRelatedChipClick,
    messagesEndRef,
  }) => {
    const renderBotStateUI = (message: BotMessage) => {
      switch (message.state) {
        case 'complete':
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 18, color: 'green' }} />
              <Typography variant="caption">Conversation complete</Typography>
            </Box>
          );

        case 'awaiting_input':
          return (
            <Typography variant="caption" sx={{ color: '#111111', mt: 1 }}>
              ↳ Waiting for your response...
            </Typography>
          );

        case 'awaiting_confirmation':
          return (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleBotStateAction('yes')}
                disabled={isLoading}
                sx={{
                  backgroundColor: '#111111',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '8px',
                  '&:hover': { backgroundColor: '#222222' },
                  '&:focus-visible': {
                    outline: '2px solid #111111',
                    outlineOffset: '2px',
                  },
                }}
              >
                Yes
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleBotStateAction('no')}
                disabled={isLoading}
                sx={{
                  borderColor: '#EAEAEA',
                  color: '#1A1A1A',
                  textTransform: 'none',
                  borderRadius: '8px',
                  '&:hover': { borderColor: '#B0B0B0', backgroundColor: '#FAFAFA' },
                  '&:focus-visible': {
                    outline: '2px solid #111111',
                    outlineOffset: '2px',
                  },
                }}
              >
                No
              </Button>
            </Box>
          );

        case 'prefill_ui':
          return message.uiAction ? (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="contained"
                size="small"
                endIcon={<OpenInNewIcon />}
                onClick={() => handleBotStateAction('open-form', message.uiAction!.editUrl)}
                sx={{
                  backgroundColor: '#111111',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '8px',
                  mb: 0.5,
                  '&:hover': { backgroundColor: '#222222' },
                  '&:focus-visible': {
                    outline: '2px solid #111111',
                    outlineOffset: '2px',
                  },
                }}
              >
                Open Form
              </Button>
              <Typography variant="caption" sx={{ display: 'block', color: 'gray', fontSize: '10px' }}>
                Form will be pre-filled with suggested data
              </Typography>
            </Box>
          ) : null;

        default:
          return null;
      }
    };

    const renderRelatedChips = (chips?: BotMessage['relatedChips']) => {
      if (!chips || chips.length === 0) return null;

      return (
        <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ width: '100%', color: '#757575', mb: 0.5 }}>
            Related topics:
          </Typography>
          {chips.map(chip => (
            <Chip
              key={chip.id}
              label={chip.label}
              size="small"
              onClick={() => handleRelatedChipClick(chip)}
              variant="outlined"
              sx={{
                cursor: 'pointer',
                borderRadius: '8px',
                borderColor: '#E0E0E0',
                fontSize: '11px',
                backgroundColor: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#F5F5F7',
                  borderColor: '#B0B0B0',
                },
                '&:focus-visible': {
                  outline: '2px solid #111111',
                  outlineOffset: '2px',
                },
              }}
            />
          ))}
        </Box>
      );
    };

    return (
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          backgroundColor: '#FFFFFF',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0, 0, 0, 0.1)',
            borderRadius: '10px',
          },
        }}
      >
        {/* Greetings & Quick Actions - Aligned as scrollable top content */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 2.5,
            mb: 1,
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '16px',
              backgroundColor: '#111111',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              animation: isActivating ? 'geminiPulse 1.8s ease-in-out infinite' : 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            }}
          >
            <SmartToyIcon sx={{ fontSize: 32 }} />
          </Box>
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                fontSize: '18px',
                lineHeight: 1.3,
                color: '#1A1A1A',
                mb: 0.5,
                px: 1,
              }}
            >
              Hello 👋, How can I help you with your shipment today?
            </Typography>
            <Typography variant="body2" sx={{ color: '#757575', fontSize: '13px' }}>
              Pick a quick action or just ask me anything.
            </Typography>
          </Box>

          {/* Grid of Quick Actions */}
          <Grid container spacing={1.5} sx={{ width: '100%', mt: 0.5 }}>
            {QUICK_ACTIONS.map(action => (
              <Grid item xs={6} key={action.id}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleQuickAction(action)}
                  aria-label={`Quick action: ${action.label}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1.5,
                    padding: '12px 14px',
                    borderRadius: '12px',
                    border: '1px solid #EAEAEA',
                    backgroundColor: '#FFFFFF',
                    textTransform: 'none',
                    color: '#1A1A1A',
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.01)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#B0B0B0',
                      backgroundColor: '#FAFAFA',
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.03)',
                      transform: 'translateY(-1px)',
                    },
                    '&:focus-visible': {
                      outline: '2px solid #111111',
                      outlineOffset: '2px',
                      borderColor: '#111111',
                    },
                  }}
                >
                  <Box sx={{ color: '#1A1A1A', display: 'flex', alignItems: 'center' }}>{action.icon}</Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '13px',
                      textAlign: 'left',
                      lineHeight: 1.2,
                    }}
                  >
                    {action.label}
                  </Typography>
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Conversation Messages */}
        {messages.map(message => (
          <Fade key={message.id} in>
            {message.role === 'user' ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', maxWidth: '75%' }}>
                  <Box
                    sx={{
                      backgroundColor: '#111111',
                      color: '#FFFFFF',
                      padding: '12px 16px',
                      borderRadius: '20px 20px 4px 20px',
                      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '13.5px', lineHeight: 1.5 }}>
                      {message.content}
                    </Typography>

                    {message.attachmentPreviews && message.attachmentPreviews.length > 0 && (
                      <Box sx={{ marginTop: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {message.attachmentPreviews.map((file, idx) => (
                          <Chip
                            key={idx}
                            icon={<FilePresentIcon sx={{ color: '#FFFFFF !important' }} />}
                            label={`${file.name.substring(0, 15)}...`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.7rem',
                              height: 'auto',
                              padding: '4px 8px',
                              color: '#FFFFFF',
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', marginTop: 0.5, marginRight: 0.5, color: '#9E9E9E', fontSize: '11px' }}
                  >
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    backgroundColor: '#111111',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    flexShrink: 0,
                  }}
                >
                  <SmartToyIcon sx={{ fontSize: 16 }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '75%' }}>
                  <Box
                    sx={{
                      backgroundColor: '#F5F5F7',
                      color: '#1A1A1A',
                      padding: '12px 16px',
                      borderRadius: '20px 20px 20px 4px',
                      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '13.5px', lineHeight: 1.5 }}>
                      {message.content}
                    </Typography>

                    {renderBotStateUI(message)}
                    {renderRelatedChips(message.relatedChips)}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', marginTop: 0.5, marginLeft: 0.5, color: '#9E9E9E', fontSize: '11px' }}
                  >
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>
            )}
          </Fade>
        ))}

        {/* Loading Indicator */}
        {isLoading && (
          <Fade in>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  backgroundColor: '#111111',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  flexShrink: 0,
                }}
              >
                <SmartToyIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box
                sx={{
                  backgroundColor: '#F5F5F7',
                  padding: '12px 16px',
                  borderRadius: '20px 20px 20px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={16} sx={{ color: '#111111' }} />
                <Typography variant="caption" sx={{ color: '#757575', fontSize: '12px' }}>
                  Thinking...
                </Typography>
              </Box>
            </Box>
          </Fade>
        )}
        <div ref={messagesEndRef} />
      </Box>
    );
  },
);

ChatMessages.displayName = 'ChatMessages';
